import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PoliceDepartmentPicker } from '@/components/police';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ReadPoliceDepartment } from '@/services/api/generated/policeDepartmentsService';
import {
  usePoliceReportsCreateMutation,
  usePoliceReportsDestroyMutation,
  usePoliceReportsListQuery,
  usePoliceReportsPartialUpdateMutation,
} from '@/services/api/policeReportsService';
import type {
  IngestionSection,
  IngestionSections,
  IngestionSectionStatus,
} from '@/services/types/case-ingestion-types';

import { useUnifiedFlow } from '../../context/UnifiedFlowContext';
import { useResetIngestionSections, useStepSave } from '../../hooks';
import { IngestionNotFoundBanner, IngestionStepLayout } from '../../layout';
import { IngestionStepSkeleton } from '../../skeletons';

const policeReportSchema = z.object({
  report_identifier: z.string().optional(),
  police_department_id: z.string().optional(),
});

type PoliceReportFormData = z.infer<typeof policeReportSchema>;

interface SelectedDepartment {
  id: string;
  name: string;
  stateCode?: string;
}

interface LiabilityPoliceReportStepProps {
  matterId: string;
  sectionStatuses: IngestionSections;
  onSaveComplete?: (success: boolean) => void;
}

export const LiabilityPoliceReportStep: React.FC<LiabilityPoliceReportStepProps> = ({
  matterId,
  sectionStatuses,
  onSaveComplete,
}) => {
  const { requestNavigateToStep } = useUnifiedFlow();
  const { saveStep } = useStepSave();
  const { resetSections } = useResetIngestionSections();
  // Initialize notFoundChecked from audit record (sectionStatuses guaranteed loaded via parent gate)
  const [notFoundChecked, setNotFoundChecked] = useState(
    () => sectionStatuses.police_report === 'not_found'
  );
  const hasHydrated = useRef(false);
  const [existingReportId, setExistingReportId] = useState<string | null>(null);

  const [createPoliceReport] = usePoliceReportsCreateMutation();
  const [deletePoliceReport] = usePoliceReportsDestroyMutation();
  const [updatePoliceReport] = usePoliceReportsPartialUpdateMutation();

  // Track the selected department for display
  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectedDepartment | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<PoliceReportFormData>({
    resolver: zodResolver(policeReportSchema),
  });

  // Fetch existing police reports
  const { data: existingReports, isLoading: isLoadingReports } = usePoliceReportsListQuery(
    { matterId },
    { skip: !matterId },
  );

  // Hydrate form from existing report
  useEffect(() => {
    if (hasHydrated.current) return;
    if (!existingReports?.length) return;

    hasHydrated.current = true;
    const report = existingReports[0];
    setExistingReportId(report.id);
    if (report.report_identifier) {
      setValue('report_identifier', report.report_identifier);
    }
    if (report.police_department?.id) {
      setValue('police_department_id', report.police_department.id);
      setSelectedDepartment({
        id: report.police_department.id,
        name: report.police_department.name || 'Unknown Department',
        stateCode: report.police_department.state_code || undefined,
      });
    }
  }, [existingReports, setValue]);

  // Auto-fill from CMS import data (runs once after hydration)
  const { importData } = useUnifiedFlow();
  const hasAutoFilledFromImport = useRef(false);
  useEffect(() => {
    if (hasAutoFilledFromImport.current) return;
    if (!importData?.payload?.liability) return;
    // Wait for hydration to complete if existing reports are still loading
    if (existingReports === undefined) return;
    if (existingReports.length > 0 && !hasHydrated.current) return;

    hasAutoFilledFromImport.current = true;
    const liab = importData.payload.liability;

    // Fill police report number if empty
    if (liab.police_report_number.presence === 'present' && liab.police_report_number.value) {
      const currentVal = getValues('report_identifier');
      if (!currentVal) {
        setValue('report_identifier', liab.police_report_number.value);
      }
    }
  }, [importData, existingReports, setValue, getValues]);

  const handleDepartmentSelected = (department: ReadPoliceDepartment) => {
    setValue('police_department_id', department.id);
    setSelectedDepartment({
      id: department.id,
      name: department.name || 'Unknown Department',
      stateCode: department.state_code || undefined,
    });
  };

  const handleClearDepartment = async () => {
    if (existingReportId) {
      try {
        await deletePoliceReport({ matterId, id: existingReportId }).unwrap();
      } catch {
        toast.error('Failed to remove police report');
        return;
      }
    }

    setExistingReportId(null);
    setValue('police_department_id', undefined);
    setValue('report_identifier', '');
    setSelectedDepartment(null);
    setNotFoundChecked(false);

    try {
      await resetSections(['police_report']);
    } catch {
      toast.error('Failed to reset police report section');
    }
  };

  const handleNotFoundToggle = (checked: boolean) => {
    setNotFoundChecked(checked);
  };

  const onSubmit = async (data: PoliceReportFormData) => {
    const policeDepartmentId = data.police_department_id;
    const hasPoliceReport = Boolean(policeDepartmentId);

    // No-op save: user hasn't marked not-found and hasn't selected a department yet.
    // We intentionally do not toast or update audit state here.
    if (!notFoundChecked && !hasPoliceReport) {
      onSaveComplete?.(true);
      return;
    }

    // Only update section status if user made an explicit choice:
    // - Checked "not found" checkbox → 'not_found'
    // - Added a police report → 'found'
    // - Neither → don't update (stays null/unreviewed)
    const sectionUpdates: Partial<Record<IngestionSection, IngestionSectionStatus>> = {};
    if (notFoundChecked) {
      sectionUpdates.police_report = 'not_found';
    } else if (hasPoliceReport) {
      sectionUpdates.police_report = 'found';
    }

    const success = await saveStep(
      async () => {
        // Only create police report if we have data
        if (hasPoliceReport && !notFoundChecked) {
          if (!policeDepartmentId) {
            return;
          }

          if (existingReportId) {
            await updatePoliceReport({
              matterId,
              id: existingReportId,
              patchedUpdatePoliceReport: {
                id: existingReportId,
                police_department_id: policeDepartmentId ?? null,
                report_identifier: data.report_identifier || null,
              },
            }).unwrap();
          } else {
            if (!policeDepartmentId) return;
            const createdReport = await createPoliceReport({
              matterId,
              createPoliceReport: {
                matter_id: matterId,
                police_department_id: policeDepartmentId,
                report_identifier: data.report_identifier || undefined,
                attestation_status: 'attested_unverified',
              },
            }).unwrap();
            setExistingReportId(createdReport.id);
          }
        }
      },
      sectionUpdates,
      {
        successMessage: 'Police report information saved',
        errorMessage: 'Failed to save police report information',
      },
    );
    onSaveComplete?.(success);
  };

  // Show skeleton during initial load
  if (isLoadingReports && !hasHydrated.current) {
    return <IngestionStepSkeleton />;
  }

  return (
    <IngestionStepLayout
      title="Police Report"
      description="Enter police report details if available"
    >
      {/* Not found checkbox - shown when no department selected */}
      {!selectedDepartment && (
        <div className="flex justify-end">
          <IngestionNotFoundBanner
            id="police-report-not-found"
            checked={notFoundChecked}
            onCheckedChange={handleNotFoundToggle}
            label="Police report not available"
          />
        </div>
      )}

      <form
        id="ingestion-step-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-2"
      >
        {!notFoundChecked && (
          <Card>
            <CardContent className="space-y-2 p-2">
              {!selectedDepartment ? (
                // State 1: No department selected - show picker + disabled report number
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Police Department</Label>
                    <div className="mt-1 [&_.space-y-3]:space-y-1.5 [&_input]:h-8 [&_input]:text-xs">
                      <PoliceDepartmentPicker
                        selectedDepartmentId={null}
                        onDepartmentSelect={() => {}}
                        onDepartmentSelected={handleDepartmentSelected}
                        onDepartmentCreated={handleDepartmentSelected}
                        placeholder="Search departments..."
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="report_identifier_disabled" className="text-xs">
                      Report Number
                    </Label>
                    <Input
                      id="report_identifier_disabled"
                      disabled
                      placeholder="Select a department to add the report number"
                      className="mt-1 h-8 text-xs"
                    />
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                      Select a police department to add the report number. If you don’t know the
                      department yet,{' '}
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs font-normal text-blue-600 dark:text-blue-400"
                        onClick={() => requestNavigateToStep(8)}
                      >
                        save it in General Notes
                      </Button>
                      .
                    </p>
                  </div>
                </div>
              ) : (
                // State 2: Department selected - show card with nested report number
                <div className="space-y-3">
                  {/* Department header with remove button */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-zinc-500" />
                      <div>
                        <p className="text-xs font-medium text-zinc-900">
                          {selectedDepartment.name}
                        </p>
                        {selectedDepartment.stateCode && (
                          <p className="text-[11px] text-zinc-500">
                            {selectedDepartment.stateCode}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-zinc-500 hover:text-zinc-700"
                      onClick={handleClearDepartment}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Report Number - nested under department */}
                  <div className="pl-6">
                    <Label htmlFor="report_identifier" className="text-xs">
                      Report Number
                    </Label>
                    <Input
                      id="report_identifier"
                      {...register('report_identifier')}
                      placeholder="Enter report number (optional)"
                      className="mt-1 h-8 text-xs"
                    />
                    {errors.report_identifier && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.report_identifier.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </form>
    </IngestionStepLayout>
  );
};
